import { isNil } from 'lodash';
import { createContext, useCallback, useContext } from 'react';
import { FieldProvenance, FieldProvenanceRecord } from './types';

export interface FieldProvenanceContextValue {
  /** Which of the form's fields were filled by the agent, and how surely. */
  provenance: FieldProvenanceRecord;
  /**
   * Puts one field back the way it was before any proposal touched it.
   *
   * Optional because a form can report marks without being able to undo them — a preview, or a test
   * that only cares what the mark says. A field whose form left this out shows the mark and no
   * revert control, rather than a control that would do nothing.
   */
  revertField?: (componentId: string) => void;
}

/**
 * Which of the form's fields were filled by the agent, how surely, and how to put one back.
 *
 * Defaulted to empty rather than thrown on, for the same reason as `FieldConflictContext`: every
 * form renders `EditFieldContainer`, and all but the create form have no proposals to report. A
 * form without a provider is not broken — nothing proposed anything into it.
 */
export const FieldProvenanceContext = createContext<FieldProvenanceContextValue>({
  provenance: {},
});

/** The mark to show against one component, or undefined when the user's own value is there. */
export function useFieldProvenance(componentId: string): FieldProvenance | undefined {
  return useContext(FieldProvenanceContext).provenance[componentId];
}

/**
 * How to undo the proposal on one component, or undefined when there is nothing to undo.
 *
 * Gated on the mark rather than on the cache, because the two are the same thing: the form caches
 * what a proposal replaced at the moment it marks the field, and drops both together the moment the
 * user edits it. So a field with a mark always has somewhere to go back to, and a field without one
 * never does.
 */
export function useFieldRevert(componentId: string): (() => void) | undefined {
  const { provenance, revertField } = useContext(FieldProvenanceContext);
  const isRevertible = !isNil(revertField) && !isNil(provenance[componentId]);
  const revert = useCallback(() => revertField?.(componentId), [revertField, componentId]);
  return isRevertible ? revert : undefined;
}
